// PKCE helpers (RFC 7636) for the OAuth 2.1 authorization-code flow.
// The Bitol authorization server requires PKCE for all public clients; we use
// S256 exclusively. The verifier is generated client-side, persisted in
// sessionStorage during the redirect, and exchanged with the authorization
// code at /oauth2/token.
//
// Ported from Workbench origin/main@60e779e:src/utils/pkce.js with the storage
// key renamed and the authorize-URL builder kept identical.

const VERIFIER_BYTES = 32  // → 43 base64url chars, within the 43-128 RFC range
const STATE_BYTES = 16
const STORAGE_KEY_PREFIX = 'next.oauth.pkce.'
const RETURN_TO_PREFIX = 'next.oauth.returnTo.'

function base64UrlEncode(bytes) {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomBytes(n) {
  const a = new Uint8Array(n)
  crypto.getRandomValues(a)
  return a
}

export function generateCodeVerifier() {
  return base64UrlEncode(randomBytes(VERIFIER_BYTES))
}

export async function deriveCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(digest))
}

export function generateState() {
  return base64UrlEncode(randomBytes(STATE_BYTES))
}

// Persist verifier (+ optional returnTo path) under the state-keyed entry.
// The callback page reads it back by state, deletes it, then exchanges.
export function persistPkce(state, verifier, returnTo) {
  sessionStorage.setItem(STORAGE_KEY_PREFIX + state, verifier)
  if (returnTo) sessionStorage.setItem(RETURN_TO_PREFIX + state, returnTo)
}

export function consumePkce(state) {
  const vKey = STORAGE_KEY_PREFIX + state
  const rKey = RETURN_TO_PREFIX + state
  const verifier = sessionStorage.getItem(vKey)
  const returnTo = sessionStorage.getItem(rKey)
  sessionStorage.removeItem(vKey)
  sessionStorage.removeItem(rKey)
  return { verifier, returnTo }
}

export function buildAuthorizeUrl({ issuer, clientId, redirectUri, state, codeChallenge, scope, resource }) {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })
  if (scope) params.set('scope', scope)
  // RFC 8707 Resource Indicators — Bitol AS rejects authorize requests that
  // don't carry a registered `resource` URI.
  if (resource) params.set('resource', resource)
  return `${issuer.replace(/\/$/, '')}/oauth2/authorize?${params.toString()}`
}
